import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Loader as Loader2 } from 'lucide-react';
import { authClient } from './auth-client';
import { useAuth } from './auth-context';
import { fetchWithAuth } from '@/lib/auth/fetch-with-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/platform/ui/card';
import { Button } from '@/platform/ui/button';

const RECHECK_EVERY_MS = 30000;

interface PendingApprovalScreenProps {
  email: string;
  /** Asks the guard to look at the account status again. */
  onRecheck: () => Promise<void> | void;
}

/**
 * Shown to people who have signed up but haven't been approved yet.
 * It sends the welcome email once (the server makes sure it only goes out once)
 * and checks every 30 seconds so the page moves on by itself after approval.
 */
export function PendingApprovalScreen({ email, onRecheck }: PendingApprovalScreenProps) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [checking, setChecking] = useState(false);
  const [stillPending, setStillPending] = useState(false);
  const welcomeSent = useRef(false);

  useEffect(() => {
    if (!session || welcomeSent.current) return;
    welcomeSent.current = true;
    fetchWithAuth('/api/auth/signup-welcome', session, { method: 'POST' }).catch(() => {
      // The welcome email is a nice-to-have; never block the screen on it.
    });
  }, [session]);

  useEffect(() => {
    const timer = setInterval(() => {
      onRecheck();
    }, RECHECK_EVERY_MS);
    return () => clearInterval(timer);
  }, [onRecheck]);

  const handleCheckAgain = async () => {
    setChecking(true);
    setStillPending(false);
    try {
      await onRecheck();
      setStillPending(true);
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.auth.signOut();
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Clock className="h-16 w-16 text-primary" />
          </div>
          <CardTitle>Thanks for signing up</CardTitle>
          <CardDescription>Your account is waiting for approval</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm">We approve new accounts by hand. Once we've had a look, we'll email:</p>
            <p className="font-medium text-primary break-all">{email}</p>
            <p className="text-sm text-muted-foreground">
              You can close this page. If you leave it open, it'll move on by itself once you're approved.
            </p>
          </div>

          {stillPending && (
            <p className="text-sm text-center text-muted-foreground" role="status">
              Not approved yet. We'll email you as soon as it's done.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Button onClick={handleCheckAgain} disabled={checking} className="w-full">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check again'}
            </Button>
            <Button variant="ghost" onClick={handleSignOut} className="w-full">
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
